/**
 * Pure reconciliation logic for the address-book server sync (#476), split out
 * from the React hook so it can be unit-tested without network/DOM.
 */

export type AddressBookSyncDecision =
  | 'pull' // server is strictly newer and we have no un-synced local edits
  | 'push' // local has changes (or the server has no copy yet) — upload it
  | 'conflict' // server is strictly newer AND we have un-synced local edits
  | 'noop'; // already in sync

export interface AddressBookSyncInputs {
  /** Whether the server currently holds a copy. */
  serverExists: boolean;
  /** The server's current version (0 when it holds no copy). */
  serverVersion: number;
  /** The last server version this device synced to (0 = never). */
  lastSyncedVersion: number;
  /** Whether the local book differs from what this device last pushed/pulled. */
  hasLocalChanges: boolean;
  /** How many contacts are in the local book. */
  localContactCount: number;
  /**
   * Whether the server's copy was last written by THIS device. A server version
   * ahead of ours is then our own push whose response we never saw (the request
   * was cut off after the server committed it — a restart, a dropped connection,
   * a gateway timeout), not a write from somewhere else.
   */
  serverLastWrittenByThisDevice: boolean;
}

/**
 * Decide what the next sync step should be. A server strictly ahead of our last
 * synced version wins only when we have no un-synced edits to lose; otherwise it
 * is a divergence the user must resolve — unless we can see the write was ours,
 * in which case there is nothing to resolve and we simply push again.
 */
export function decideAddressBookSync(
  inputs: AddressBookSyncInputs
): AddressBookSyncDecision {
  const {
    serverExists,
    serverVersion,
    lastSyncedVersion,
    hasLocalChanges,
    localContactCount,
    serverLastWrittenByThisDevice,
  } = inputs;

  if (serverExists && serverVersion > lastSyncedVersion) {
    // Only a NON-EMPTY local book that differs from the last sync is a real
    // divergence to warn about. An empty local book (e.g. a fresh device, or
    // cleared local storage) has no un-synced edits to protect, so just restore
    // the server copy rather than popping a conflict prompt.
    if (!hasLocalChanges || localContactCount === 0) return 'pull';

    // Our own un-acknowledged push: re-push over it (at the version the server
    // just reported) so the bookkeeping catches up. Nothing diverged, so the
    // user has nothing to decide.
    return serverLastWrittenByThisDevice ? 'push' : 'conflict';
  }

  const shouldPush =
    hasLocalChanges || (!serverExists && localContactCount > 0);
  return shouldPush ? 'push' : 'noop';
}
